import { baseApi } from "./baseApi";

const loansApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getLoanDetail: builder.query({
      query: (loanId) => ({
        url: `/loans/${loanId}`,
        method: "GET",
      }),
      providesTags: (result, error, loanId) => [
        { type: "Loan", id: loanId },
      ],
    }),
    patchLoan: builder.mutation({
      query: ({ loanId, data }) => ({
        url: `/loans/${loanId}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { loanId }) => [
        { type: "Loan", id: loanId },
      ],
    }),
    // Prepay lives on the loan and the pricing engine reads the penalty from there rather than
    // from the quote request, so the Pricing tab writes a term change straight through instead of
    // waiting for a Loan File save. Only the prepay fields are touched.
    updateLoanPrepay: builder.mutation({
      query: ({ loanId, prepaymentPenaltyIndicator, prepaymentExpirationMonthCount }) => ({
        url: `/loans/${loanId}/nonqm/prepay`,
        method: "PATCH",
        body: { prepaymentPenaltyIndicator, prepaymentExpirationMonthCount },
      }),
      invalidatesTags: (result, error, { loanId }) => [
        { type: "Loan", id: loanId },
      ],
    }),
    submitLoan: builder.mutation({
      query: (loanId) => ({
        url: `/loans/${loanId}/submit-loan`,
        method: "POST",
      }),
      invalidatesTags: (result, error, loanId) => [
        { type: "Loan", id: loanId },
      ],
    }),
    submitDocOrderRequest: builder.mutation({
      query: ({ loanId, body }) => ({
        url: `/loans/${loanId}/doc-order-request`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { loanId }) => [
        { type: "Loan", id: loanId },
      ],
    }),
    // Message to the AE or AM on the loan. The recipient is named by role — the server resolves the
    // address off the loan's own team — and it also writes a copy to the loan file as a note.
    sendLoanTeamMessage: builder.mutation({
      query: ({ loanId, recipientRoleKey, message }) => ({
        url: `/loans/${loanId}/team-message`,
        method: "POST",
        body: { recipientRoleKey, message },
      }),
    }),
    importMismo: builder.mutation({
      query: ({ file, loanType, loanChannel, brokerUserId, processorUserId }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("loanType", loanType || "Mortgage");
        formData.append("loanChannel", loanChannel || "Wholesale");
        if (brokerUserId) formData.append("brokerUserId", brokerUserId);
        if (processorUserId) formData.append("processorUserId", processorUserId);
        return {
          url: "/loans/import-mismo",
          method: "POST",
          body: formData,
        };
      },
    }),
    // Create a wholesale loan from the loan-file shape (modal sends borrowers[0] name only).
    createLoan: builder.mutation({
      query: ({ borrowers, loanType, brokerUserId, processorUserId }) => ({
        url: "/loans",
        method: "POST",
        body: {
          borrowers,
          loanType: loanType || "Mortgage",
          ...(brokerUserId && { brokerUserId }),
          ...(processorUserId && { processorUserId }),
        },
      }),
    }),
    previewMismoAdvisor: builder.mutation({
      query: ({ file, lockTerm, compensationType, productFamilies, terms, confirmedFico, targetPrice }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("lockTerm", String(lockTerm ?? 30));
        formData.append("compensationType", compensationType || "LPC");
        if (productFamilies?.length) {
          formData.append("productFamilies", productFamilies.join(","));
        }
        if (terms?.length) {
          formData.append("terms", terms.join(","));
        }
        if (confirmedFico != null) {
          formData.append("confirmedFico", String(confirmedFico));
        }
        if (targetPrice != null && targetPrice !== '') {
          formData.append("targetPrice", String(targetPrice));
        }
        return {
          url: "/loans/mismo/advisor/preview",
          method: "POST",
          body: formData,
        };
      },
    }),
    recommendMismoAdvisor: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `/loans/${loanId}/mismo/advisor/recommend`,
        method: "POST",
        body,
      }),
    }),
    acceptMismoAdvisor: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `/loans/${loanId}/mismo/advisor/accept`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { loanId }) => [
        { type: "Loan", id: loanId },
      ],
    }),
    assignProduct: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `/loans/${loanId}/pricing/assign-product`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { loanId }) => [
        { type: "Loan", id: loanId },
      ],
    }),
    // Assign then lock: backend waits until program/rate/price settle on the loan
    // before requesting the lock. Same body as assign-product.
    assignAndLockProduct: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `/loans/${loanId}/pricing/assign-and-lock`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { loanId }) => [
        { type: "Loan", id: loanId },
      ],
    }),
  }),
});

export default loansApi;
